import { Component, createContext, type ErrorInfo, type PropsWithChildren, type ReactElement, useContext } from "react"

export const ErrorBoundaryContext = createContext<Error | string | null>(null)

export function useError() {
	return useContext(ErrorBoundaryContext)
}

export interface ErrorBoundaryProps {
	onError?: (error: Error, errorInfo: ErrorInfo) => void
	fallback: ReactElement | null
}

export interface ErrorBoundaryState {
	hasError: boolean
	error: Error | null
}

export class ErrorBoundary extends Component<
	PropsWithChildren<ErrorBoundaryProps>,
	ErrorBoundaryState
> {
	state: ErrorBoundaryState = {
		hasError: false,
		error: null
	}

	componentDidCatch(error, errorInfo) {
		this.props.onError?.(error, errorInfo)
	}

	static getDerivedStateFromError(error) {
		return { error, hasError: true }
	}

	render() {
		if (this.state.hasError) {
			return (
				<ErrorBoundaryContext.Provider value={this.state.error}>
					{this.props.fallback}
				</ErrorBoundaryContext.Provider>
			)
		}

		return this.props.children
	}
}

